import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal } from 'antd';
import { FD130 } from '../../services/pwd212Api';
import { usePWD212Mode } from './hooks/usePWD212Mode';
import { useLookupNames } from './hooks/useLookupNames';
import { usePWD212Record } from './hooks/usePWD212Record';
import { usePWD212Navigation } from './hooks/usePWD212Navigation';
import { usePWD212Permissions } from './hooks/usePWD212Permissions';
import PermissionGuard from '../../components/PermissionGuard';
import Tab1BasicInfo from './components/Tab1BasicInfo';
import Tab2ProductSpec from './components/Tab2ProductSpec';
import Tab3Material from './components/Tab3Material';
import Tab4ColorDetail from './components/Tab4ColorDetail';
import ActionBar from './components/ActionBar';
import { getStatusBadge, isFieldDisabledFn } from './helpers';
import './PWD212Form.css';

const PWD212Form: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);

  // Hooks
  const perms = usePWD212Permissions();
  const { mode, setMode, isEditing } = usePWD212Mode();
  const { names, resolveAllNames, resolveSingleName, clearNames } = useLookupNames();
  const {
    record, formData, details, productDesc, compositions,
    selectedRow, loading,
    setSelectedRow, setCompositions,
    loadRecord, setField, getField,
    setPDField, getPDField,
    getDetailField, setDetailField,
    handleSave, handleDelete,
    addDetailRow, deleteDetailRow,
    addCompositionRow, deleteCompositionRow,
    revertToRecord, resetForAdd, applyResult,
    getFieldError,
  } = usePWD212Record();

  // Wrap loadRecord to also resolve names
  const loadRecordWithNames = useCallback(async (id: string) => {
    const result = await loadRecord(id);
    if (result) {
      resolveAllNames(result.data, result.pd);
    }
  }, [loadRecord, resolveAllNames]);

  const { allIds, currentIdx, setCurrentIdx, loadList, navTo } = usePWD212Navigation(loadRecordWithNames);

  // Initial load
  useEffect(() => {
    (async () => {
      try {
        const ids = await loadList();
        if (ids.length > 0) {
          setCurrentIdx(0);
          await loadRecordWithNames(ids[0]);
        }
      } catch {
        console.error('Failed to load PWD212 list');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadList, loadRecordWithNames, setCurrentIdx]);

  // Mode actions
  const handleAddAction = useCallback(async () => {
    if (isEditing) {
      await handleSave(mode, async (result: FD130) => {
        if (mode === 'add') {
          const ids = await loadList();
          const newIdx = ids.indexOf(result.fd130_01);
          setCurrentIdx(newIdx >= 0 ? newIdx : 0);
        }
        applyResult(result);
        setMode('browse');
      });
    } else {
      setMode('add');
      resetForAdd();
      setActiveTab(0);
      clearNames();
    }
  }, [isEditing, mode, handleSave, loadList, setCurrentIdx, applyResult, setMode, resetForAdd, clearNames]);

  const handleModifyAction = useCallback(() => {
    if (isEditing) {
      revertToRecord();
      setMode('browse');
    } else {
      if (!record) return;
      setMode('edit');
    }
  }, [isEditing, record, revertToRecord, setMode]);

  const handleDeleteAction = useCallback(() => {
    if (!record || isEditing) return;
    Modal.confirm({
      title: '確認刪除',
      content: `確定要刪除計劃託工單 ${record.fd130_01}？`,
      okText: '刪除',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        const success = await handleDelete();
        if (success) {
          const ids = await loadList();
          if (ids.length > 0) {
            const nextIdx = Math.min(currentIdx, ids.length - 1);
            setCurrentIdx(nextIdx);
            await loadRecordWithNames(ids[nextIdx]);
          } else {
            resetForAdd();
            setCurrentIdx(-1);
          }
        }
      },
    });
  }, [record, isEditing, handleDelete, loadList, currentIdx, setCurrentIdx, loadRecordWithNames, resetForAdd]);

  const handleNavTo = useCallback((idx: number) => {
    navTo(idx, isEditing);
  }, [navTo, isEditing]);

  // Computed
  const statusBadge = getStatusBadge(record);
  const isFieldDisabled = useCallback(
    (key: string) => isFieldDisabledFn(key, isEditing, mode),
    [isEditing, mode]
  );

  return (
    <PermissionGuard module={'pwd212f1'} action="query">
    <div className="pwd212-form">
      {loading && <div className="loading-overlay">載入中...</div>}

      <div className="page-wrapper">
        {/* Page Title Bar */}
        <div className="page-title-bar">
          <div className="page-title-left">
            <nav className="breadcrumb-inline">
              <a href="#/erp" onClick={e => { e.preventDefault(); navigate('/erp'); }}>首頁</a>
              <span>/</span>
              <span className="current">計劃託工單</span>
            </nav>
            <span className="title-separator">--</span>
            <h1>計劃託工單 Planned Subcontract Order</h1>
            <span className="badge badge-default">D212</span>
            {statusBadge && (
              <span className={`badge ${statusBadge.cls}`}>{statusBadge.text}</span>
            )}
          </div>
        </div>

        {/* Form Info Bar */}
        <div className="form-info-bar">
          <div className="field-group">
            <label>託工單號</label>
            <input type="text" readOnly style={{ width: 140 }} value={formData.fd130_01 || ''} />
          </div>
          <div className="field-group">
            <label>加工廠</label>
            <span style={{ fontSize: 13 }}>{names.processor || '--'}</span>
          </div>
          <div className="field-group">
            <label>胚布預估單號</label>
            <span style={{ fontSize: 13 }}>{formData.fd130_78 || '--'}</span>
          </div>
        </div>

        {/* Tabs */}
        <div className="tabs">
          <div className="tab-nav">
            {['基本資料', '產品規格', '原料/品質/備註', '色號明細'].map((label, i) => (
              <button
                key={i}
                className={`tab-nav-item ${activeTab === i ? 'active' : ''}`}
                onClick={() => setActiveTab(i)}
              >
                {label}
              </button>
            ))}
          </div>

          <div className={`tab-pane ${activeTab === 0 ? 'active' : ''}`}>
            <Tab1BasicInfo
              formData={formData} productDesc={productDesc}
              names={names} isEditing={isEditing}
              isFieldDisabled={isFieldDisabled} getField={getField} setField={setField}
              getPDField={getPDField} setPDField={setPDField}
              resolveSingleName={resolveSingleName} getFieldError={getFieldError}
            />
          </div>

          <div className={`tab-pane ${activeTab === 1 ? 'active' : ''}`}>
            <Tab2ProductSpec
              productDesc={productDesc} names={names} isEditing={isEditing}
              getPDField={getPDField} setPDField={setPDField}
              resolveSingleName={resolveSingleName} isFieldDisabled={isFieldDisabled}
            />
          </div>

          <div className={`tab-pane ${activeTab === 2 ? 'active' : ''}`}>
            <Tab3Material
              formData={formData} productDesc={productDesc}
              compositions={compositions} isEditing={isEditing}
              isFieldDisabled={isFieldDisabled} getField={getField} setField={setField}
              getPDField={getPDField} setPDField={setPDField}
              resolveSingleName={resolveSingleName} names={names}
              setCompositions={setCompositions}
              addCompositionRow={addCompositionRow} deleteCompositionRow={deleteCompositionRow}
            />
          </div>

          <div className={`tab-pane ${activeTab === 3 ? 'active' : ''}`}>
            <Tab4ColorDetail
              formData={formData} details={details} selectedRow={selectedRow}
              isEditing={isEditing} setSelectedRow={setSelectedRow}
              getDetailField={getDetailField} setDetailField={setDetailField}
              addDetailRow={addDetailRow} deleteDetailRow={deleteDetailRow}
            />
          </div>
        </div>

        {/* Bottom Action Bar */}
        <ActionBar
          mode={mode} isEditing={isEditing} record={record}
          currentIdx={currentIdx} allIdsLength={allIds.length}
          permissions={perms}
          onAdd={handleAddAction} onModify={handleModifyAction}
          onDelete={handleDeleteAction} onNavTo={handleNavTo}
          onExit={() => navigate('/erp')}
        />
      </div>
    </div>
    </PermissionGuard>
  );
};

export default PWD212Form;
